import type {
  Ticket,
  TicketMessage,
  TicketPriority,
  TicketStatus,
} from './types';
import type { TicketDto, TicketMessageDto } from './dto';
import { ProjectStatus } from '../../../types/project';

const STATUS_MAP: Record<string, TicketStatus> = {
  accepted: 'new',
  in_queue: 'run',
  completed: 'done',
};

/** Приоритет: старое поле criticality (low/medium/high) → словарь прототипа. */
const PRIORITY_MAP: Record<string, TicketPriority> = {
  low: 'normal',
  normal: 'normal',
  medium: 'high',
  high: 'high',
  critical: 'critical',
};

/** Срок ответа бэк отдаёт числом дней от создания — считаем дату сами. */
const toDecisionDate = (createdAt?: string, days?: number | null): string => {
  if (!createdAt || days == null) return '—';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '—';
  date.setDate(date.getDate() + days);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export const toStatus = (value?: string): TicketStatus => {
  if (!value) return 'new';
  const mapped = STATUS_MAP[value.trim().toLowerCase()];
  if (mapped) return mapped;

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[support] неизвестный статус: "${value}" — показан как «Новое»`);
  }
  return 'new';
};

export const toPriority = (value?: string): TicketPriority => {
  if (!value) return 'normal';
  return PRIORITY_MAP[value.trim().toLowerCase()] ?? 'normal';
};

const pad = (n: number): string => String(n).padStart(2, '0');

const toDate = (raw?: string): string => {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const toDateTime = (raw?: string): string => {
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return `${toDate(raw)} в ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const mapTicket = (dto: TicketDto): Ticket => ({
  id: dto.id,
  problem: dto.problem ?? '',
  description: dto.description ?? '',
  status: toStatus(dto.status),
  priority: toPriority(dto.criticality),
  organization: dto.organization ?? '',
  name: dto.name ?? '',
  email: dto.email ?? '',
  createdAt: toDate(dto.created_at),
  deadline: toDate(dto.deadline),
  projectId: dto.project_id ?? '',
  projectName: dto.project ?? '',
  // files приходит объектом { имя: ссылка } — разворачиваем в массив
  attachments: Object.entries(dto.files ?? {}).map(([name, url]) => ({ name, url })),
  comment: dto.comment ?? '',
  userName: dto.user_name ?? '',
  decisionDate: toDecisionDate(dto.created_at, dto.decision_days),
});

export const mapTicketMessage = (dto: TicketMessageDto): TicketMessage => ({
  id: dto.id,
  text: dto.text ?? '',
  createdAt: toDateTime(dto.created_at),
  isSupport: dto.is_support_message ?? false,
});
